import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import {
  Upload, FileText, Loader2, CheckCircle2, AlertTriangle,
  ArrowDownCircle, ArrowUpCircle, Trash2, RefreshCw,
  Filter, FileQuestion, X, Sparkles,
} from 'lucide-react';
import ReviewTransactionsModal from '../components/ReviewTransactionsModal';

const FLAG_LABELS = {
  unlogged_invoice: { label: 'Unlogged invoice', color: 'bg-amber-100 text-amber-700' },
  staff_payment: { label: 'Staff payment', color: 'bg-orange-100 text-orange-700' },
  possible_staff_payment: { label: 'Possible staff payment', color: 'bg-orange-100 text-orange-700' },
  personal_transfer: { label: 'Personal transfer', color: 'bg-purple-100 text-purple-700' },
  duplicate: { label: 'Duplicate', color: 'bg-red-100 text-red-700' },
  needs_review: { label: 'Needs review', color: 'bg-blue-100 text-blue-700' },
};

const ALL_FLAGS = ['unlogged_invoice', 'staff_payment', 'personal_transfer', 'duplicate', 'needs_review'];

const CLASS_COLORS = {
  income: 'bg-green-100 text-green-700',
  expense: 'bg-slate-100 text-slate-700',
  payroll: 'bg-blue-100 text-blue-700',
  rent: 'bg-indigo-100 text-indigo-700',
  utility: 'bg-cyan-100 text-cyan-700',
  tax: 'bg-rose-100 text-rose-700',
  transfer: 'bg-purple-100 text-purple-700',
  fee: 'bg-amber-100 text-amber-700',
  personal: 'bg-pink-100 text-pink-700',
};

export default function BankStatementsPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [statements, setStatements] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reconciling, setReconciling] = useState(null);
  const [selectedStatement, setSelectedStatement] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [txnsLoading, setTxnsLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // all | matched | unmatched | flagged
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewStatementId, setReviewStatementId] = useState(null);

  const fetchStatements = useCallback(async () => {
    try {
      const res = await api.get('/bank-statements');
      setStatements(res.data);
    } catch { /* empty */ }
    finally { setLoading(false); }
  }, [api]);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await api.get('/bank-statements/summary');
      setSummary(res.data);
    } catch { /* empty */ }
  }, [api]);

  useEffect(() => { fetchStatements(); fetchSummary(); }, [fetchStatements, fetchSummary]);

  const reconcile = async (statementId) => {
    setReconciling(statementId);
    try {
      const res = await api.post(`/bank-statements/${statementId}/reconcile`);
      const { matched, total_outflows, flagged, staff_payments_logged } = res.data;
      let msg = `Matched ${matched} of ${total_outflows} outflows. ${flagged} flagged.`;
      if (staff_payments_logged > 0) {
        msg += ` ${staff_payments_logged} staff payment${staff_payments_logged === 1 ? '' : 's'} added to expenses.`;
      }
      toast.success(msg);
      fetchStatements();
      fetchSummary();
      if (selectedStatement === statementId) loadTransactions(statementId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reconcile failed');
    } finally { setReconciling(null); }
  };

  const deleteStatement = async (statementId) => {
    if (!window.confirm('Delete this statement and all its transactions?')) return;
    try {
      await api.delete(`/bank-statements/${statementId}`);
      toast.success('Statement deleted');
      if (selectedStatement === statementId) {
        setSelectedStatement(null);
        setTransactions([]);
      }
      fetchStatements();
      fetchSummary();
    } catch { toast.error('Delete failed'); }
  };

  const loadTransactions = async (statementId) => {
    setSelectedStatement(statementId);
    setTxnsLoading(true);
    try {
      const params = { statement_id: statementId };
      if (filter === 'matched') params.match_status = 'matched';
      if (filter === 'unmatched') params.match_status = 'unmatched';
      if (filter === 'flagged') params.has_flag = true;
      if (filter === 'reviewed') params.reviewed = true;
      if (filter === 'unreviewed') params.reviewed = false;
      const res = await api.get('/bank-statements/transactions', { params });
      setTransactions(res.data);
    } catch { toast.error('Failed to load transactions'); }
    finally { setTxnsLoading(false); }
  };

  useEffect(() => {
    if (selectedStatement) loadTransactions(selectedStatement);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const updateClassification = async (txnId, classification) => {
    try {
      await api.put(`/bank-statements/transactions/${txnId}`, { classification });
      setTransactions(prev => prev.map(t => t.id === txnId ? { ...t, classification } : t));
    } catch { toast.error('Update failed'); }
  };

  const updateFlags = async (txnId, newFlags) => {
    try {
      await api.put(`/bank-statements/transactions/${txnId}`, { flags: newFlags });
      setTransactions(prev => prev.map(t => t.id === txnId ? { ...t, flags: newFlags } : t));
      fetchSummary();
    } catch { toast.error('Update failed'); }
  };

  const removeFlag = (txn, flag) => {
    const next = (txn.flags || []).filter(f => f !== flag);
    updateFlags(txn.id, next);
  };

  const addFlag = (txn, flag) => {
    if ((txn.flags || []).includes(flag)) return;
    updateFlags(txn.id, [...(txn.flags || []), flag]);
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="bank-statements-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="w-6 h-6" /> Bank Statements
          </h1>
          <p className="text-muted-foreground text-sm">Import statements, reconcile against logged expenses, and surface unaccounted spend.</p>
        </div>
        <div className="flex items-center gap-2">
          {summary && summary.unreviewed > 0 && (
            <Button
              onClick={() => { setReviewStatementId(null); setReviewOpen(true); }}
              variant="default"
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="open-review-btn"
            >
              <Sparkles className="w-4 h-4 mr-1" />
              Review {summary.unreviewed}
            </Button>
          )}
          <input
            id="bank-upload-input"
            type="file"
            accept=".csv,.pdf"
            className="hidden"
            data-testid="upload-input"
            disabled
          />
          <Button
            variant="outline"
            onClick={() => navigate('/imports?tab=bank')}
            data-testid="open-imports-btn"
          >
            <Upload className="w-4 h-4 mr-1" />
            Import statement
          </Button>
        </div>
      </div>

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="summary-cards">
          <Card><CardContent className="py-3 text-center">
            <p className="text-lg font-bold" data-testid="summary-total">{summary.total_transactions}</p>
            <p className="text-xs text-muted-foreground">Total transactions</p>
          </CardContent></Card>
          <Card><CardContent className="py-3 text-center">
            <p className="text-lg font-bold text-blue-600" data-testid="summary-reviewed">
              {summary.total_transactions - (summary.unreviewed || 0)}
              <span className="text-xs text-muted-foreground font-normal"> / {summary.total_transactions}</span>
            </p>
            <p className="text-xs text-muted-foreground">Reviewed</p>
          </CardContent></Card>
          <Card><CardContent className="py-3 text-center">
            <p className="text-lg font-bold text-green-600" data-testid="summary-matched">{summary.matched}</p>
            <p className="text-xs text-muted-foreground">Reconciled to expense</p>
          </CardContent></Card>
          <Card><CardContent className="py-3 text-center">
            <p className="text-lg font-bold text-amber-600" data-testid="summary-unmatched">{summary.unmatched}</p>
            <p className="text-xs text-muted-foreground">Unreconciled</p>
          </CardContent></Card>
          <Card><CardContent className="py-3 text-center">
            <p className="text-lg font-bold text-orange-600" data-testid="summary-flagged">{summary.flagged}</p>
            <p className="text-xs text-muted-foreground">Flagged</p>
          </CardContent></Card>
          <Card><CardContent className="py-3 text-center">
            <p className="text-lg font-bold text-red-600" data-testid="summary-unaccounted">£{summary.unaccounted_total_gbp.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">Unaccounted spend</p>
          </CardContent></Card>
        </div>
      )}

      {/* Statement list */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Imported statements</CardTitle></CardHeader>
        <CardContent>
          {statements.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground" data-testid="empty-state">
              <FileQuestion className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No bank statements imported yet.</p>
              <p className="text-xs mt-1">Click <span className="font-medium">Import statement</span> to open the Imports hub.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {statements.map(s => (
                <div
                  key={s.id}
                  className={`flex items-center justify-between p-3 rounded-md border transition-colors ${selectedStatement === s.id ? 'bg-accent border-primary' : 'hover:bg-muted/50'}`}
                  data-testid={`statement-${s.id}`}
                >
                  <button onClick={() => loadTransactions(s.id)} className="flex-1 text-left">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{s.filename}</span>
                      <Badge variant="outline" className="text-[10px]">{s.source.toUpperCase()}</Badge>
                      {s.status === 'parsing' && (
                        <Badge className="text-[10px] bg-blue-100 text-blue-700">
                          <Loader2 className="w-2.5 h-2.5 mr-0.5 animate-spin" /> Parsing…
                        </Badge>
                      )}
                      {s.status === 'failed' && <Badge className="text-[10px] bg-red-100 text-red-700">Failed</Badge>}
                      {s.status === 'reconciled' && <Badge className="text-[10px] bg-green-100 text-green-700">Reconciled</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {s.status === 'parsing' ? (
                        <>AI is extracting transactions — this can take 1-3 minutes.</>
                      ) : s.status === 'failed' ? (
                        <>Error: {s.error || 'unknown'}</>
                      ) : (
                        <>
                          {s.period_start} → {s.period_end} · {s.txn_count} txns ·{' '}
                          <span className="text-green-600">+£{(s.total_in || 0).toLocaleString()}</span> /{' '}
                          <span className="text-red-600">−£{(s.total_out || 0).toLocaleString()}</span>
                          {s.reviewed_count !== undefined && (
                            <span className="ml-2">· <span className="text-blue-600">{s.reviewed_count} reviewed</span>, <span className="text-green-600">{s.matched_count || 0} reconciled</span>, <span className="text-orange-600">{s.flagged_count || 0} flagged</span></span>
                          )}
                        </>
                      )}
                    </p>
                  </button>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setReviewStatementId(s.id); setReviewOpen(true); }}
                      data-testid={`review-${s.id}`}
                    >
                      <Sparkles className="w-3 h-3 mr-1" /> Review
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => reconcile(s.id)}
                      disabled={reconciling === s.id}
                      data-testid={`reconcile-${s.id}`}
                    >
                      {reconciling === s.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                      Reconcile
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteStatement(s.id)}
                      data-testid={`delete-${s.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Transactions */}
      {selectedStatement && (
        <Card data-testid="txn-panel">
          <CardHeader className="pb-2 flex-row items-center justify-between">
            <CardTitle className="text-base">Transactions</CardTitle>
            <div className="flex items-center gap-1 flex-wrap">
              <Filter className="w-3 h-3 text-muted-foreground" />
              {['all', 'reviewed', 'unreviewed', 'matched', 'unmatched', 'flagged'].map(f => (
                <Button
                  key={f}
                  size="sm"
                  variant={filter === f ? 'default' : 'ghost'}
                  className="h-7 text-xs capitalize"
                  onClick={() => setFilter(f)}
                  data-testid={`filter-${f}`}
                >
                  {f}
                </Button>
              ))}
            </div>
          </CardHeader>
          <CardContent>
            {txnsLoading ? (
              <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
            ) : transactions.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No transactions</p>
            ) : (
              <div className="space-y-1.5">
                {transactions.map(t => {
                  const isOut = t.amount < 0;
                  const cls = CLASS_COLORS[t.classification] || 'bg-slate-100 text-slate-700';
                  return (
                    <div
                      key={t.id}
                      className="flex items-center gap-3 p-2 rounded hover:bg-muted/40 border-l-2 transition-colors"
                      style={{ borderLeftColor: isOut ? '#ef4444' : '#10b981' }}
                      data-testid={`txn-${t.id}`}
                    >
                      <div className="shrink-0">
                        {isOut
                          ? <ArrowDownCircle className="w-4 h-4 text-red-500" />
                          : <ArrowUpCircle className="w-4 h-4 text-green-500" />}
                      </div>
                      <div className="text-xs text-muted-foreground w-20 shrink-0">{t.date}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{t.payee_name || '(unknown)'}</div>
                        <div className="text-xs text-muted-foreground truncate">{t.description}</div>
                      </div>
                      <div className="flex items-center gap-1 flex-wrap">
                        <select
                          className={`text-[10px] px-1.5 py-0.5 rounded border-0 ${cls}`}
                          value={t.classification || 'expense'}
                          onChange={(e) => updateClassification(t.id, e.target.value)}
                          data-testid={`class-select-${t.id}`}
                        >
                          {Object.keys(CLASS_COLORS).map(c => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                        {(t.flags || []).map(f => (
                          <span
                            key={f}
                            className={`inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded ${FLAG_LABELS[f]?.color || 'bg-gray-100'}`}
                            data-testid={`flag-${t.id}-${f}`}
                          >
                            <AlertTriangle className="w-2.5 h-2.5" />
                            {FLAG_LABELS[f]?.label || f}
                            <button
                              onClick={() => removeFlag(t, f)}
                              className="ml-0.5 hover:opacity-70"
                              title="Remove flag"
                              data-testid={`remove-flag-${t.id}-${f}`}
                            >
                              <X className="w-2.5 h-2.5" />
                            </button>
                          </span>
                        ))}
                        <select
                          className="text-[10px] px-1 py-0.5 rounded bg-muted/50 text-muted-foreground border-0 cursor-pointer"
                          value=""
                          onChange={(e) => { if (e.target.value) addFlag(t, e.target.value); e.target.value = ''; }}
                          data-testid={`add-flag-${t.id}`}
                          title="Add flag"
                        >
                          <option value="">+ flag</option>
                          {ALL_FLAGS.filter(f => !(t.flags || []).includes(f)).map(f => (
                            <option key={f} value={f}>{FLAG_LABELS[f]?.label || f}</option>
                          ))}
                        </select>
                        {t.match_status === 'matched' && (
                          <Badge className="text-[10px] bg-green-100 text-green-700">
                            <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" /> Reconciled
                          </Badge>
                        )}
                        {t.reviewed && t.match_status !== 'matched' && (
                          <Badge className="text-[10px] bg-blue-100 text-blue-700">
                            <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" /> Reviewed
                          </Badge>
                        )}
                      </div>
                      <div className={`text-sm font-mono font-semibold w-24 text-right shrink-0 ${isOut ? 'text-red-600' : 'text-green-600'}`}>
                        {isOut ? '−' : '+'}£{Math.abs(t.amount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Separator />
      <p className="text-xs text-muted-foreground text-center">
        CSV statements parse free. PDF statements use AI ({5} credits per import). Reconciliation uses 3 credits per run.
      </p>

      <ReviewTransactionsModal
        open={reviewOpen}
        statementId={reviewStatementId}
        onClose={() => setReviewOpen(false)}
        onComplete={() => { fetchStatements(); fetchSummary(); if (selectedStatement) loadTransactions(selectedStatement); }}
      />
    </div>
  );
}
